import { z } from 'zod';

/**
 * Phasen einer Anlagenbau-Abwicklung (ADR 0004; Phasenfolge überarbeitet
 * 2026-07-31, VB-26009). Reihenfolge hier = Reihenfolge im DB-Enum: neue Werte
 * werden **angehängt**, weil ein Postgres-Enum nur so wachsen kann, ohne dass
 * `prisma migrate diff` Drift meldet. Die Anzeige-Reihenfolge steckt in
 * `PHASE_TYPE_LABELS` (Prozessreihenfolge), nicht hier.
 *
 * `PROCUREMENT`/`COMMISSIONING` sind **Altbestand**: sie gehören nicht mehr zur
 * Standardfolge, existieren aber an bestehenden Projekten und bleiben deshalb
 * gültige Werte (ein Enum-Wert lässt sich nicht gefahrlos entfernen, solange
 * Zeilen ihn tragen).
 */
export const projectPhaseTypeSchema = z.enum([
  'ENGINEERING', // Engineering (Konstruktion/Auslegung)
  'PROCUREMENT', // Beschaffung — Altbestand
  'MANUFACTURING', // Fertigung
  'ASSEMBLY', // Montage
  'COMMISSIONING', // Inbetriebnahme — Altbestand
  'ACCEPTANCE', // Abnahme
  'PROJECT_HANDOVER', // Projektübergabe (Vertrieb → Projektleitung)
  'PLAN_APPROVAL', // Planfreigabe (Zeichnungsfreigabe durch den Kunden)
  'PICKING', // Kommissionierung (Warenzusammenstellung, NICHT Inbetriebnahme)
  'TRANSPORT', // Transport zur Baustelle
  'DELIVERY', // Lieferung / Anlieferung vor Ort
]);
export type ProjectPhaseType = z.infer<typeof projectPhaseTypeSchema>;

/**
 * Bezeichnungen in der Phasen-Verwaltung. Die Reihenfolge dieses Objekts ist die
 * Anzeige-Reihenfolge (Auswahllisten, Vorlage): erst die Standardfolge, dann der
 * Altbestand. Einzige Quelle für die deutschen Typnamen — vorher lagen dieselben
 * Tabellen dreifach im Web und einmal in der API (§2.6).
 */
export const PHASE_TYPE_LABELS: Record<ProjectPhaseType, string> = {
  PROJECT_HANDOVER: 'Projektübergabe',
  ENGINEERING: 'Engineering',
  PLAN_APPROVAL: 'Planfreigabe',
  MANUFACTURING: 'Fertigung',
  PICKING: 'Kommissionierung',
  TRANSPORT: 'Transport',
  DELIVERY: 'Lieferung',
  ASSEMBLY: 'Montage',
  ACCEPTANCE: 'Abnahme',
  // Altbestand — nicht Teil der Standardfolge, weiter auswählbar für alte Projekte.
  PROCUREMENT: 'Beschaffung',
  COMMISSIONING: 'Inbetriebnahme',
};

/** Typen, die nur noch für Bestandsdaten existieren (in Auswahllisten separat gruppiert). */
export const LEGACY_PHASE_TYPES: readonly ProjectPhaseType[] = ['PROCUREMENT', 'COMMISSIONING'];

export function phaseTypeLabel(type: ProjectPhaseType): string {
  return PHASE_TYPE_LABELS[type];
}

/**
 * Standard-Phasenfolge eines PROBIG-Projekts (Tech-Lead 2026-07-31, VB-26009) —
 * Grundlage für „Standardphasen anlegen". Die vorherige Folge (Engineering →
 * Beschaffung → Fertigung → Montage → Inbetriebnahme → Abnahme) traf den
 * tatsächlichen Ablauf nicht: Beschaffung läuft im Einkaufsmodul, eine
 * Inbetriebnahme-Phase gibt es fachlich nicht, dafür fehlten Übergabe,
 * Planfreigabe, Kommissionierung, Transport und Lieferung.
 */
export const DEFAULT_PROJECT_PHASE_TYPES: readonly ProjectPhaseType[] = [
  'PROJECT_HANDOVER',
  'ENGINEERING',
  'PLAN_APPROVAL',
  'MANUFACTURING',
  'PICKING',
  'TRANSPORT',
  'DELIVERY',
  'ASSEMBLY',
  'ACCEPTANCE',
];

/** Standardfolge mit Bezeichnung — was „Standardphasen anlegen" erzeugt. */
export const DEFAULT_PROJECT_PHASES: readonly { type: ProjectPhaseType; name: string }[] =
  DEFAULT_PROJECT_PHASE_TYPES.map((type) => ({ type, name: PHASE_TYPE_LABELS[type] }));

export const phaseStatusSchema = z.enum(['PLANNED', 'ACTIVE', 'DONE']);
export type PhaseStatus = z.infer<typeof phaseStatusSchema>;

/** Phasen-Gate (Prozess-Doku §1). */
export const phaseGateSchema = z.enum(['OFFEN', 'BEANTRAGT', 'FREIGEGEBEN']);
export type PhaseGate = z.infer<typeof phaseGateSchema>;

/** Gate setzen — PM-Override mit Begründung (Audit, ADR 0013). */
export const setPhaseGateSchema = z.object({
  gate: phaseGateSchema,
  note: z.string().trim().max(2000).optional(),
});
export type SetPhaseGateInput = z.infer<typeof setPhaseGateSchema>;

export const createProjectPhaseSchema = z.object({
  name: z.string().trim().min(1).max(200),
  type: projectPhaseTypeSchema,
  status: phaseStatusSchema.default('PLANNED'),
  /** Reihenfolge; wenn weggelassen, hängt der Server die Phase hinten an. */
  sequence: z.number().int().min(0).optional(),
  plannedStart: z.string().datetime().optional(),
  plannedEnd: z.string().datetime().optional(),
});
export type CreateProjectPhaseInput = z.infer<typeof createProjectPhaseSchema>;

export const updateProjectPhaseSchema = createProjectPhaseSchema.partial();
export type UpdateProjectPhaseInput = z.infer<typeof updateProjectPhaseSchema>;

/** Verschieben einer Phase um eine Position. */
export const movePhaseSchema = z.object({ direction: z.enum(['up', 'down']) });
export type MovePhaseInput = z.infer<typeof movePhaseSchema>;

/**
 * Termine einer Phase neu setzen — auf Wunsch mit allen späteren, nicht
 * erledigten Phasen (Wunsch 2026-10-06: „Wenn es Verschiebungen gibt, sollen die
 * Phasen danach verschoben werden"). Gerechnet wird mit `shiftFollowingPhases`
 * (`phase-plan.ts`) — dieselbe Funktion zeigt die Vorschau im Dialog, damit
 * gespeichert wird, was angezeigt wurde.
 *
 * Anders als `updateProjectPhaseSchema` sind beide Daten Pflicht und `null`
 * leert sie ausdrücklich. Ende vor Beginn weist der Server mit 400 ab.
 */
export const reschedulePhaseSchema = z.object({
  plannedStart: z.string().datetime().nullable(),
  plannedEnd: z.string().datetime().nullable(),
  /** Spätere, nicht erledigte Phasen mit Daten um dieselbe Zahl Kalendertage mitschieben. */
  shiftFollowing: z.boolean(),
});
export type ReschedulePhaseInput = z.infer<typeof reschedulePhaseSchema>;

/**
 * Standardphasen (oder eine Auswahl daraus) in einem Aufruf anlegen — statt je
 * Phase ein POST aus dem Browser (VB-26009). Idempotent gedacht: Typen, die das
 * Projekt schon hat, werden übersprungen; `types` weggelassen = ganze Standardfolge.
 */
export const applyPhaseTemplateSchema = z.object({
  types: z.array(projectPhaseTypeSchema).min(1).max(50).optional(),
});
export type ApplyPhaseTemplateInput = z.infer<typeof applyPhaseTemplateSchema>;

export const projectPhaseResponseSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string(),
  type: projectPhaseTypeSchema,
  status: phaseStatusSchema,
  gate: phaseGateSchema,
  gateNote: z.string().nullable(),
  sequence: z.number().int(),
  plannedStart: z.string().datetime().nullable(),
  plannedEnd: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ProjectPhaseResponse = z.infer<typeof projectPhaseResponseSchema>;
